import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaSearch, FaPlus, FaList, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { API_BASE_URL } from '../../utils/apiConfig';
import { hasPermission } from '../../utils/permissions';
const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
    margin-bottom: 25px;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.7rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.95rem;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #e9ecef;
    color: #2c3e50;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
  
  &:after {
    content: ${props => props.required ? '" *"' : '""'};
    color: #dc3545;
  }
`;

const Select = styled.select`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SubmitButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ResetButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const DaysPreferredContainer = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CheckboxInput = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const HeaderText = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #2c3e50;
  color: white;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-right: 1px solid #34495e;
  
  &:last-child {
    border-right: none;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  border-bottom: 1px solid #e9ecef;
`;

const Td = styled.td`
  padding: 15px 20px;
  color: #495057;
  font-size: 0.95rem;
  border-right: 1px solid #e9ecef;
  
  &:last-child {
    border-right: none;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#dc3545' : 'rgb(51, 66, 147)'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 5px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    margin-right: 8px;
    min-width: 40px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 0.9rem;
    margin-right: 10px;
    min-width: 44px;
    height: 40px;
  }
`;

const ActionCell = styled.td`
  width: 120px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: auto;
    min-width: 140px;
    text-align: left;
    padding: 15px 20px;
  }
  
  @media (max-width: 480px) {
    min-width: 160px;
    padding: 15px 20px;
  }
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? '#28a745' : '#6c757d'};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const CheckboxLabel = styled.label`
  margin-left: 5px;
`;

const ExampleText = styled.span`
  font-size: 0.8rem;
  color: #6c757d;
`;

const StatusText = styled.span`
  background: ${props => props.active ? '#28a745' : '#6c757d'};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const TableCheckbox = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const DaysPreferred = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreateProfile = hasPermission(user, 'profile_create');
  const canUpdateProfile = hasPermission(user, 'profile_update');
  const canDeleteProfile = hasPermission(user, 'profile_delete');

  const [selectedDays, setSelectedDays] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [daysPreferredData, setDaysPreferredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    dayName: '',
    dayCode: '',
    isActive: true
  });

  const { confirmState, showDeleteConfirm } = useConfirm();

  // Fetch days preferred from API
  const fetchDaysPreferred = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/profile/days-preferred`);
      const data = await response.json();
      if (data.success) {
        setDaysPreferredData(data.daysPreferred);
      }
    } catch (error) {
      console.error('Error fetching days preferred:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDaysPreferred();
  }, []);

  const openModal = (day = null) => {
    if (day) {
      setEditingDay(day);
      setFormData({
        dayName: day.day_name || '',
        dayCode: day.day_code || '',
        isActive: day.is_active
      });
    } else {
      setEditingDay(null);
      setFormData({
        dayName: '',
        dayCode: '',
        isActive: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDay(null);
    setFormData({
      dayName: '',
      dayCode: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dayName.trim()) {
      showError('Please enter a day name');
      return;
    }

    setLoading(true);
    try {
      const url = editingDay 
        ? `${API_BASE_URL}/profile/days-preferred/${editingDay.id}`
        : `${API_BASE_URL}/profile/days-preferred`;
      
      const method = editingDay ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dayName: formData.dayName,
          isActive: formData.isActive,
          dayCode: formData.dayCode
        }),
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Day preferred updated successfully');
        closeModal();
        fetchDaysPreferred();
      } else {
        showError(result.message || 'Failed to update day preferred');
      }
    } catch (error) {
      showError('Error updating day preferred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirm(
      daysPreferredData.find(day => day.id === id)?.day_name || 'Day Preferred',
      'day preferred'
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/profile/days-preferred/${id}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Day preferred deleted successfully');
        fetchDaysPreferred();
      } else {
        showError(result.message || 'Failed to delete day preferred');
      }
    } catch (error) {
      showError('Error deleting day preferred');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (editingDay) {
      setFormData({
        dayName: editingDay.day_name || '',
        dayCode: editingDay.day_code || '',
        isActive: editingDay.is_active
      });
    } else {
      setFormData({
        dayName: '',
        dayCode: '',
        isActive: true
      });
    }
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedDays(daysPreferredData.map(day => day.id));
    } else {
      setSelectedDays([]);
    }
  };

  const handleSelectDay = (dayId) => {
    setSelectedDays(prev => {
      if (prev.includes(dayId)) {
        return prev.filter(id => id !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading days preferred...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Days Preferred</Title>
        <ActionButtons>
          {canCreateProfile && (
            <AddButton onClick={() => openModal()}>
              <FaPlus /> &nbsp; &nbsp; Add New Day Preferred
            </AddButton>
          )}
        </ActionButtons>
      </Header>

      <DaysPreferredContainer>
        <TableHeader>
          {/* <TableCheckbox
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          /> */}
          <HeaderText>Days Preferred Records</HeaderText>
        </TableHeader>

        <Table style={{display: 'inline-table'}}>
          <Thead>
            <Tr>
              <Th style={{ width: '50px' }}></Th>
              <Th>Day Name</Th>
              <Th>Day Code</Th>
              <Th>Status</Th>
              <Th style={{ width: '120px' }}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {daysPreferredData.map((day) => (
              <Tr key={day.id}>
                <Td>
                  {/* <TableCheckbox
                    type="checkbox"
                    checked={selectedDays.includes(day.id)}
                    onChange={() => handleSelectDay(day.id)}
                  /> */}
                </Td>
                <Td>{day.day_name}</Td>
                <Td>{day.day_code}</Td>
                <Td>
                  <StatusText active={day.is_active}>
                    {day.is_active ? 'Active' : 'Inactive'}
                  </StatusText>
                </Td>
                <ActionCell>
                  {canUpdateProfile && (
                    <ActionButton onClick={() => openModal(day)}>
                      <FaEdit />
                    </ActionButton>
                  )}
                  {canDeleteProfile && (
                    <ActionButton danger onClick={() => handleDelete(day.id)}>
                      <FaTrash />
                    </ActionButton>
                  )}
                </ActionCell>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </DaysPreferredContainer>

      {/* Custom Confirmation Dialog */}
      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        details={confirmState.details}
        itemName={confirmState.itemName}
      />

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingDay ? 'Edit Day Preferred' : 'Add Day Preferred'}</ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label required>Day Name</Label>
                  <Input
                    type="text"
                    name="dayName"
                    value={formData.dayName}
                    onChange={handleInputChange}
                    placeholder="e.g., Monday"
                    required
                  />
                  <ExampleText>Example: Monday, Tuesday, Wednesday, Thursday, Friday</ExampleText>
                </FormGroup>

                <FormGroup>
                  <Label>Day Code</Label>
                  <Input
                    type="text"
                    name="dayCode"
                    value={formData.dayCode}
                    onChange={handleInputChange}
                    placeholder="e.g. MON, TUE, WED"
                  />
                </FormGroup>

                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Set as Active Day</CheckboxLabel>
                </CheckboxContainer>

                <ButtonGroup>
                  <ResetButton type="button" onClick={handleReset}>
                    Reset
                  </ResetButton>
                  {(editingDay ? canUpdateProfile : canCreateProfile) && (
                    <SubmitButton type="submit" disabled={loading}>
                      {loading ? 'Saving...' : (editingDay ? 'Update' : 'Add')}
                    </SubmitButton>
                  )}
                </ButtonGroup>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default DaysPreferred; 